// ManageCourses.tsx
import React, { useEffect, useState } from "react";
import "./ManageCourses.css";
import ReusableAccordion from "../../../components/reusable-accordian/ReusableAccordion";
import ReusableTable, {
  Column,
} from "../../../components/reusable-table/ReusableTable";
import Button from "../../../components/reusable-button/Button";
import CourseModal, { CourseFormData } from "./course-modal/CourseModal";
import { BiEditAlt } from "react-icons/bi";
import {
  getAllCategories,
  getAllCategoryTypes,
} from "../../../apis/categoryApis";
import {
  getAllModules,
  createModule,
  updateModule,
} from "../../../apis/courseApis";
import { showSnackbar } from "../../../redux/slices/snackbarSlice";
import { useDispatch } from "react-redux";
import { getFileUrl } from "../../../utils/getFileUrl";
import ReusableLoader from "../../../components/reusable-loader/ReusableLoader";

const EditIcon = BiEditAlt as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

interface CourseRow {
  id: string;
  title: string;
  subtitle: string;
  logo: string | undefined;
  description: string;
  status: "Active" | "Inactive";
  links: { linkDisplayName: string; url: string }[];
  provider: string;
  category: string | null;
}

interface CourseCategory {
  id: string;
  title: string;
  logo: string | undefined;
  provider: string;
  categoryStatus: "Active" | "Inactive";
  courses: CourseRow[];
}

const ManageCourses: React.FC = () => {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editData, setEditData] = useState<CourseFormData | undefined>();
  const [editModuleId, setEditModuleId] = useState<string | null>(null);
  const [courseCategories, setCourseCategories] = useState<CourseCategory[]>(
    []
  );
  const [categoryTypeOptions, setCategoryTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string; categoryTypeId: string }[]
  >([]);
  const [loadData, setLoadData] = useState(false);
  const [loading, setLoading] = useState(false);

  const adminDetail = localStorage.getItem("adminDetail");
  const roleName = adminDetail
    ? JSON.parse(adminDetail)?.roleIds?.roleName
    : null;

  const buildColumns = (catTitle: string): Column<CourseRow>[] => {
    const baseColumns: Column<CourseRow>[] = [
      {
        header: "Course",
        render: (c) => <span className="course-title">{c.title}</span>,
      },
      { header: "Description", accessor: "description" },
      {
        header: "Status",
        render: (c) => (
          <span
            className={`status ${
              c.status === "Active" ? "active" : "inactive"
            }`}
          >
            {c.status === "Inactive" ? "Inactive" : c.status}
          </span>
        ),
      },
    ];

    const actionColumn: Column<CourseRow> = {
      header: "",
      render: (c) => {
        const matchingCategory = categoryOptions.find(
          (co) => co.label === c.category
        );
        const matchingCategoryType = categoryTypeOptions.find(
          (cto) => cto.label === c.provider
        );

        return (
          <button
            className="edit-button"
            onClick={() => {
              setModalMode("edit");
              setEditModuleId(c.id);
              setEditData({
                category: matchingCategory?.value || "",
                categoryType: matchingCategoryType?.value || "",
                courseName: c.title,
                description: c.description,
                active: c.status === "Active",
                courseLearningLink: c.links,
              });
              setModalOpen(true);
            }}
          >
            <EditIcon style={{ fontSize: 16, color: "#28606B" }} />
          </button>
        );
      },
    };

    return roleName === "admin" ? baseColumns : [...baseColumns, actionColumn];
  };

  const fetchData = async () => {
    setLoadData(true);
    try {
      const [categoryTypesRes, allCoursesRes, modulesRes] = await Promise.all([
        getAllCategoryTypes(),
        getAllCategories(),
        getAllModules(),
      ]);

      const categoryTypes = categoryTypesRes.data;
      const allCourses = allCoursesRes.data;
      const modules = modulesRes.data;

      const categoryTypeOptionsMapped = categoryTypes.map((type: any) => ({
        label: type.categoriesTypeName,
        value: type.id,
      }));
      setCategoryTypeOptions(categoryTypeOptionsMapped);

      const categoryOptionsMapped = allCourses.map((course: any) => ({
        label: course.courseName,
        value: course.id,
        categoryTypeId: course.categoryTypeId,
      }));
      setCategoryOptions(categoryOptionsMapped);

      const structuredData: CourseCategory[] = [];

      for (const course of allCourses) {
        const categoryType = categoryTypes.find(
          (type: any) => type.id === course.categoryTypeId
        );
        if (!categoryType) continue;

        const matchingModules = modules.filter(
          (m: any) => m.courseId === course.id
        );

        let logoUrl: string | undefined;
        // if (item.files?.uuid) {
        logoUrl = await getFileUrl(course.files.uuid);

        structuredData.push({
          id: course.id,
          provider: categoryType.categoriesTypeName,
          title: course.courseName,
          logo: logoUrl,
          categoryStatus: course.isActive ? "Active" : "Inactive",
          courses: matchingModules.map((module: any) => ({
            id: module.id,
            title: module.moduleName,
            subtitle: "",
            logo: undefined,
            description: module.moduleDescription,
            status: module.isActive ? "Active" : "Inactive",
            links: module.courseLearningLink || [],
            provider: categoryType.categoriesTypeName,
            category: course.courseName,
          })),
        });
      }

      setCourseCategories(structuredData);
    } catch (err) {
      console.error("Failed to load courses:", err);
    }
    setLoadData(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async (data: CourseFormData) => {
    setLoading(true);
    try {
      if (modalMode === "add") {
        await createModule({
          moduleName: data.courseName,
          moduleDescription: data.description,
          courseId: data.category,
          courseLearningLink: data.courseLearningLink,
        });

        dispatch(
          showSnackbar({
            message: "Course added successfully!",
            type: "success",
          })
        );
      } else if (modalMode === "edit" && editModuleId) {
        const res = await updateModule(editModuleId, {
          moduleName: data.courseName,
          moduleDescription: data.description,
          courseId: data.category,
          courseLearningLink: data.courseLearningLink,
          isActive: data.active,
        });

        dispatch(
          showSnackbar({
            message: res?.data,
            type: "success",
          })
        );
      }

      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error("Save failed:", error);
      dispatch(
        showSnackbar({
          message: "Failed to save course. Please try again.",
          type: "error",
        })
      );
    }
    setLoading(false);
    setEditData(undefined);
    setEditModuleId(null);
    setLoadData(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditData(undefined);
    setEditModuleId(null);
  };

  return (
    <div className="manage-courses">
      <div className="header">
        <h2>Manage Courses ({courseCategories?.length})</h2>
        {roleName !== "admin" && (
          <div>
            <Button
              label="+ Add Course"
              onClick={() => {
                setModalMode("add");
                setEditData(undefined);
                setEditModuleId(null);
                setModalOpen(true);
              }}
              type="button"
            />
          </div>
        )}
      </div>

      {loadData ? (
        <ReusableLoader />
      ) : (
        ["GMetrix", "General"].map((provider) => {
          const group = courseCategories.filter((c) => c.provider === provider);
          if (!group.length) return null;

          return (
            <section key={provider} className="provider-block">
              <h4 className="provider">{provider}</h4>
              {group.map((cat) => (
                <ReusableAccordion
                  key={cat.id}
                  logo={cat.logo}
                  title={cat.title}
                  status={cat.categoryStatus}
                  showStatus={true}
                >
                  <ReusableTable
                    columns={buildColumns(cat.title)}
                    rows={cat.courses}
                  />
                </ReusableAccordion>
              ))}
            </section>
          );
        })
      )}

      <CourseModal
        isOpen={modalOpen}
        mode={modalMode}
        initialData={editData}
        categoryTypeOptions={categoryTypeOptions}
        categoryOptions={categoryOptions}
        onClose={handleModalClose}
        onSave={handleSave}
        loading={loading}
      />
    </div>
  );
};

export default ManageCourses;
